import Button from '@precis-digital/kurama/Button'
import Grid from '@precis-digital/kurama/Grid'
import Input from '@precis-digital/kurama/Input'
import SearchIcon from '@precis-digital/kurama/icons/SearchIcon'
import { styled } from '@precis-digital/kurama/utils'
import { useState, useEffect, useMemo } from 'react'
import { debounce } from 'lodash'

const SEARCH_DISPLAY_SLICE_LENGTH = 4
const SEARCH_DEBOUNCE_DELAY = 300

interface SearchButtonProps {
  value: string
  handleSubmit: (value: string) => void
  handleReset?: () => void
  isDebouncedSearch?: boolean
  disabled?: boolean
  ref?: React.Ref<HTMLDivElement>
}

const SearchButton = ({
  value,
  handleSubmit,
  disabled = false,
  handleReset,
  isDebouncedSearch = false,
  ref
}: SearchButtonProps): React.ReactElement => {
  const [tempSearch, setTempSearch] = useState<string>(value)
  const [showSearchInput, setShowSearchInput] = useState(false)

  useEffect(() => {
    if (value === '') {
      setTempSearch(value)
    }
  }, [value])

  const debouncedSearch = useMemo(() => debounce(handleSubmit, SEARCH_DEBOUNCE_DELAY), [handleSubmit])
  const handleResetAndClose = (): void => {
    if (handleReset != null) {
      handleReset()
    }

    setShowSearchInput(false)
  }

  return (
    <AnimatedSearchGrid ref={ref} expanded={showSearchInput} hasSearch={value !== ''}>
      {showSearchInput ? (
        <Input
          autoFocus
          fullWidth
          value={tempSearch}
          name="search"
          icon={<SearchIcon />}
          placeholder="Search"
          disabled={disabled}
          onChange={(e) => {
            const newValue = e.target.value
            setTempSearch(newValue)
            if (isDebouncedSearch) {
              debouncedSearch(newValue)
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSubmit(tempSearch)
              setShowSearchInput(false)
            }
          }}
          onBlur={handleResetAndClose}
        />
      ) : (
        <Button
          scheme="light"
          variant="tonal"
          leftIcon={<SearchIcon />}
          disabled={disabled}
          onClick={() => {
            if (!disabled) setShowSearchInput(true)
          }}
          aria-label="Show search input"
        >
          {value.length > SEARCH_DISPLAY_SLICE_LENGTH ? `${value.slice(0, SEARCH_DISPLAY_SLICE_LENGTH)}...` : value}
        </Button>
      )}
    </AnimatedSearchGrid>
  )
}

export const AnimatedSearchGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'expanded' && prop !== 'hasSearch'
})<{ expanded: boolean; hasSearch: boolean }>(({ expanded, hasSearch, theme }) => {
  let widthValue
  if (expanded) {
    widthValue = '250px'
  } else if (hasSearch) {
    widthValue = 'min-content'
  } else {
    widthValue = '40px'
  }
  return {
    width: widthValue,
    transition:
      'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1), border-radius 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'row',
    overflow: 'hidden',
    boxShadow: expanded ? theme.elevation.mediumDepth : 'none',
    borderRadius: '16px'
  }
})

export default SearchButton
